import React from 'react';
import { Alert, Box, Chip, Typography } from '@mui/material';
import { TextField } from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import OpenInNew from '@material-ui/icons/OpenInNew';
import { FulfillmentLifecycleStatus, OrderDetail } from '../../../services/fulfillment/types';
import { LIFECYCLE_STATUSES } from '../../../services/fulfillment/constants';
import { FormatDateString } from '../../../utils/helper';
import WaveStatusChip from '../WaveStatusChip';
import { SectionLabel } from './SectionLabel';
import {
  formatAddressLines,
  formatCustomerName,
  riskLevelChipProps,
} from '../FulfillmentOrderDetail.utils';

interface Props {
  order: OrderDetail;
  orderStatus: FulfillmentLifecycleStatus | null;
  inWave: boolean;
  savingOrderStatus: boolean;
  orderStatusWarning: string | null;
  activeLineCount: number;
  onOrderStatusChange: (newStatus: FulfillmentLifecycleStatus) => void;
  onClearOrderStatusWarning: () => void;
}

export const OrderHeaderPanel: React.FC<Props> = ({
  order,
  orderStatus,
  inWave,
  savingOrderStatus,
  orderStatusWarning,
  activeLineCount,
  onOrderStatusChange,
  onClearOrderStatusWarning,
}) => {
  const customerName = formatCustomerName(order.customer);
  const addressParts = formatAddressLines(order.shippingAddress);
  const riskProps = riskLevelChipProps(order.riskLevel);

  return (
    <Box
      sx={{
        borderRight: '1px solid',
        borderColor: 'grey.300',
        p: 4,
        display: 'flex',
        flexDirection: 'column',
        gap: 3,
      }}
    >
      <Box>
        <Typography sx={{ fontSize: 18, fontWeight: 300, mb: 0 }}>Shopify Order</Typography>
        <Typography sx={{ fontSize: 28, fontWeight: 700, letterSpacing: '1px', pt: 0 }}>
          <a
            href={`https://admin.shopify.com/store/${order.shopifyStoreName}/orders/${order.shopifyOrderId}`}
            target="_blank"
            rel="noreferrer"
          >
            {order.shopifyOrderName}
          </a>
          <OpenInNew fontSize="small" color="disabled" />
        </Typography>
      </Box>
      <Box display="flex" flexWrap="wrap" gap={0.5}>
        <WaveStatusChip status={order.waveStatus} waveId={order.waveId} />
        <Chip label={order.orderType || 'Unknown'} size="small" />
        {order.hasDropShip && <Chip label="Drop Ship" size="small" />}
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        <SectionLabel>Order date</SectionLabel>
        <Typography sx={{ fontSize: 14 }}>{FormatDateString(order.createdAt)}</Typography>
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        <SectionLabel>Risk Level</SectionLabel>
        <Typography sx={{ fontSize: 14 }}>
          <Chip
            label={order.riskLevel}
            size="small"
            color={riskProps.color}
            variant={riskProps.variant}
          />
        </Typography>
      </Box>

      {order.note && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <SectionLabel>Note</SectionLabel>
          <Typography sx={{ fontSize: 14 }}>{order.note}</Typography>
        </Box>
      )}

      <Box
        component="hr"
        sx={{ border: 'none', borderTop: '1px solid', borderColor: 'grey.200', m: 0 }}
      />

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        <SectionLabel>Fulfillment Status</SectionLabel>
        <Autocomplete<FulfillmentLifecycleStatus>
          options={LIFECYCLE_STATUSES}
          value={orderStatus}
          onChange={(_, newVal) => {
            if (newVal) onOrderStatusChange(newVal);
          }}
          getOptionLabel={s => s}
          getOptionSelected={(opt, val) => opt === val}
          disabled={inWave || savingOrderStatus}
          renderInput={params => (
            <TextField {...params} variant="outlined" size="small" placeholder="—" />
          )}
        />
        {orderStatusWarning && (
          <Alert severity="warning" sx={{ fontSize: 12 }} onClose={onClearOrderStatusWarning}>
            {orderStatusWarning}
          </Alert>
        )}
      </Box>

      <Box
        component="hr"
        sx={{ border: 'none', borderTop: '1px solid', borderColor: 'grey.200', m: 0 }}
      />

      {customerName && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <SectionLabel>Customer</SectionLabel>
          <Typography sx={{ fontSize: 14 }}>{customerName}</Typography>
          {order.customer?.email && (
            <Typography sx={{ fontSize: 14 }}>{order.customer.email}</Typography>
          )}
        </Box>
      )}

      {addressParts.length > 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <SectionLabel>Ship to</SectionLabel>
          {addressParts.map((line, i) => (
            <Typography key={i} sx={{ fontSize: 14 }}>
              {line}
            </Typography>
          ))}
        </Box>
      )}

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        <SectionLabel>Items</SectionLabel>
        <Typography sx={{ fontSize: 14 }}>{activeLineCount}</Typography>
      </Box>
    </Box>
  );
};
